import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { tenantsService } from '../services/tenants.service';
import {
  TenantDetail,
  getTenantStatus,
  getTenantStatusLabel,
  etatAcces,
  libelleAcces,
  TenantStatus,
} from '../types/tenant';
import { formatCurrency, formatDate } from '../utils/format';
import '../styles/TenantsPage.css';
import { getErrorMessage } from '../utils/error';

/**
 * La LISTE des locataires. Elle liste, et ouvre.
 *
 * Le dossier — location, dates, accès, documents — et les cinq actions de
 * cycle de vie vivent sur `/tenants/:id`. Donner congé ou faire sortir
 * quelqu'un ne se décide pas depuis une cellule de tableau : c'est le partage
 * déjà retenu pour Candidats, Contacts et Agences.
 *
 * Reste ici la seule action qui porte sur l'ENSEMBLE : créer un locataire.
 */
const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();

  const [tenants, setTenants] = useState<TenantDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');

  // Création d'un locataire
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // DÉCOCHÉE par défaut : créer un compte n'écrit à personne. L'invitation
  // partait automatiquement jusqu'au 2026-08-30, ce qui privait le bailleur du
  // choix du moment — un locataire saisi à l'avance, ou dont l'adresse reste à
  // confirmer, recevait un courrier prématuré. Le lien se renvoie à tout moment
  // depuis sa fiche.
  const [envoyerMaintenant, setEnvoyerMaintenant] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!selectedColoc?.id) {
      setTenants([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setTenants(await tenantsService.listForColoc(selectedColoc.id));
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement des locataires'));
    } finally {
      setLoading(false);
    }
  }, [selectedColoc]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTenants = tenants.filter(t => {
    if (statusFilter === 'all') return true;
    return getTenantStatus(t) === statusFilter;
  });

  // --- Handlers ---


  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedColoc) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const cree = await tenantsService.createTenant({
        first_name: createFirstName,
        last_name: createLastName,
        email: createEmail,
        coloc_ids: [selectedColoc.id],
        send_welcome: envoyerMaintenant,
      });
      // Le message dit ce qui s'est RÉELLEMENT passé. L'envoi est best-effort :
      // annoncer un email parti alors qu'il a échoué laisserait le bailleur
      // attendre une connexion qui ne viendra jamais.
      setNotice(
        !envoyerMaintenant
          ? `Compte créé pour ${createEmail}. Aucun email envoyé — le lien de connexion s'envoie depuis sa fiche, une fois une chambre réservée.`
          : cree.invitationSent
            ? `Compte créé pour ${createEmail}. Le lien de connexion est parti.`
            : `Compte créé pour ${createEmail}, mais l'email n'a pas pu partir. Vérifiez l'adresse, puis renvoyez le lien depuis sa fiche, une fois une chambre réservée.`,
      );
      setShowCreateModal(false);
      setCreateFirstName('');
      setCreateLastName('');
      setCreateEmail('');
      setEnvoyerMaintenant(false);
      await loadData();
    } catch (err) {
      setCreateError(getErrorMessage(err, 'Erreur lors de la création'));
    } finally {
      setCreateLoading(false);
    }
  };
  const renderStatusBadge = (tenant: TenantDetail) => {
    const status = getTenantStatus(tenant);
    const label = getTenantStatusLabel(status);
    return <span className={`tenant-status-badge status-${status}`}>{label}</span>;
  };

  // --- Render ---

  if (loading) {
    return <div className="tenants-loading">Chargement...</div>;
  }

  if (!selectedColoc) {
    return <div className="tenants-empty">Sélectionnez une colocation</div>;
  }

  // La ligne ouverte, relue dans la liste à jour : garder l'objet en état
  // afficherait des valeurs périmées après un préavis ou un téléversement.

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="tenants-page">
      <div className="tenants-header">
        <h2>Locataires</h2>
        <div className="tenants-header-actions">
          <select
            className="tenants-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as TenantStatus | 'all')}
          >
            <option value="all">Tous les statuts</option>
            <option value="active">En place</option>
            <option value="notice">Préavis en cours</option>
            <option value="reserved">Réservé</option>
            <option value="unassigned">À loger</option>
            <option value="exited">Sorti</option>
          </select>
          <button className="tenants-add-btn" onClick={() => {
            setCreateError(null);
            setShowCreateModal(true);
          }}>
            Ajouter un locataire
          </button>
        </div>
      </div>

      {error && <div className="tenants-error">{error}</div>}
      {notice && <div className="candidats-notice">{notice}</div>}

      <div className="tenants-table-container">
        <table className="tenants-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Chambre</th>
              <th>Loyer</th>
              <th>Début bail</th>
              <th>Statut</th>
              <th>Espace locataire</th>
              <th>Dossier</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants.map(tenant => (
              <tr
                key={tenant.id}
                className="tenants-row"
                onClick={() => navigate(`/tenants/${tenant.id}`)}
              >
                <td className="tenant-name">{tenant.firstName} {tenant.lastName}</td>
                <td>{tenant.roomName || <span className="no-room">Non assigné</span>}</td>
                <td>{tenant.monthlyRent != null ? formatCurrency(tenant.monthlyRent) : '-'}</td>
                <td>{tenant.leaseStartDate ? formatDate(tenant.leaseStartDate) : '-'}</td>
                <td>{renderStatusBadge(tenant)}</td>
                {/* L'accès à côté de la location : c'est là que se voit un ancien
                    locataire dont l'espace est resté ouvert. */}
                <td>
                  <span className={`tenant-access-badge access-${etatAcces(tenant) === 'ouvert' ? 'on' : 'off'}`}>
                    {libelleAcces(etatAcces(tenant))}
                  </span>
                </td>
                {/* Rien à afficher tant que le locataire n'est pas entré : son
                    dossier se construit et n'a aucune pièce à prouver. */}
                <td>
                  {!tenant.dossierSuivi ? (
                    <span className="no-room">—</span>
                  ) : (
                    <span className={`tenant-access-badge access-${tenant.dossierManques.length === 0 ? 'on' : 'off'}`}>
                      {tenant.dossierManques.length === 0
                        ? 'Complet'
                        : `${tenant.dossierManques.length} manquante${tenant.dossierManques.length > 1 ? 's' : ''}`}
                    </span>
                  )}
                </td>
                <td className="tenants-actions" onClick={e => e.stopPropagation()}>
                  <button
                    className="btn-action btn-secondary"
                    onClick={() => navigate(`/tenants/${tenant.id}`)}
                  >
                    Ouvrir le dossier
                  </button>
                </td>
              </tr>
            ))}
            {filteredTenants.length === 0 && (
              <tr>
                <td colSpan={8} className="tenants-empty-row">
                  <span className="socle-vide-titre">Aucun locataire</span>
                  <span className="socle-vide-aide">
                    Un locataire apparaît ici dès qu'une chambre lui est réservée —
                    depuis une candidature acceptée, ou avec « Ajouter un locataire »
                    pour quelqu'un rencontré hors du site.
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {/* Create tenant modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Nouveau locataire</h3>
            {createError && <div className="tenants-error">{createError}</div>}
            <form onSubmit={handleCreateTenant} className="tenants-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom</label>
                  <input type="text" className="form-control" value={createFirstName} onChange={e => setCreateFirstName(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Nom</label>
                  <input type="text" className="form-control" value={createLastName} onChange={e => setCreateLastName(e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" className="form-control" value={createEmail} onChange={e => setCreateEmail(e.target.value)} required />
              </div>
              {/* Plus de mot de passe : le locataire choisit le sien par le
                  lien qu'il reçoit. Le bailleur ne doit pas détenir l'accès au
                  dossier d'un tiers (audit du 2026-08-17, P1-4) — la règle
                  valait déjà pour le locataire créé à la signature d'un bail. */}
              <div className="action-choix">
                <label>
                  <input
                    type="checkbox"
                    checked={envoyerMaintenant}
                    onChange={e => setEnvoyerMaintenant(e.target.checked)}
                  />
                  Envoyer tout de suite le lien de connexion
                </label>
                <p className="access-hint" style={{ marginTop: 6 }}>
                  Le lien permet au locataire de choisir son mot de passe — vous ne
                  le connaîtrez jamais. Sans cet envoi, le compte est créé mais
                  reste inutilisable jusqu&apos;à ce que vous lui envoyiez le lien
                  depuis sa fiche, une fois une chambre réservée.
                </p>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-action btn-secondary" onClick={() => setShowCreateModal(false)}>Annuler</button>
                <button type="submit" className="btn-action btn-save" disabled={createLoading}>
                  {createLoading ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;
